import {
  Button,
  Dropdown,
  type DropdownWC,
  Icon,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@pypestream/design-system';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { OrgSecret } from './maestro-org-api';
import {
  NameCell,
  ScopeCell,
  UpdatedCell,
  ValueCell,
} from './org-secret-cells';
import type { OrgSecretRow } from './org-secret-rows';
import { useSecretsText } from './org-secrets-text';

export function OrgSecretsTable({
  rows,
  orgName,
  canManage,
  canManageKeys,
  onValue,
  onEdit,
  onDelete,
}: {
  rows: OrgSecretRow[];
  orgName: string;
  canManage: boolean;
  /** A Super Admin on the root org: only they change a Pypestream key. */
  canManageKeys: boolean;
  onValue: (secret: OrgSecret) => void;
  onEdit: (secret: OrgSecret) => void;
  onDelete: (secret: OrgSecret) => void;
}) {
  const text = useSecretsText();
  const manages = (row: OrgSecretRow) =>
    row.secret !== null &&
    canManage &&
    (!row.secret.pypestreamKey || canManageKeys);

  return (
    <div className="c-org-frame">
      <table className="c-org-table" data-test-id="org-secrets-table">
        <thead>
          <tr>
            <th scope="col">{text('columns.name')}</th>
            <th scope="col">{text('columns.value')}</th>
            <th scope="col">{text('columns.scope')}</th>
            <th scope="col">{text('columns.source')}</th>
            <th scope="col">{text('columns.updated')}</th>
            <th scope="col">
              <span className="c-org-visually-hidden">
                {text('columns.actions')}
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} data-test-id="org-secret-row">
              <th scope="row">
                <NameCell row={row} />
              </th>
              <td>
                <ValueCell row={row} />
              </td>
              <td>
                <ScopeCell row={row} />
              </td>
              <td>
                {row.source === 'pypestreamKey' ? (
                  <Tooltip content={text('source.pypestreamKeyTip')}>
                    <span>{text('source.pypestreamKey')}</span>
                  </Tooltip>
                ) : row.source === 'parent' ? (
                  text('source.parent')
                ) : (
                  orgName
                )}
              </td>
              <td>
                <UpdatedCell row={row} />
              </td>
              <td>
                <span className="c-org-actions">
                  {row.secret && manages(row) ? (
                    <>
                      <Button
                        variant="secondary"
                        size="small"
                        test-id="org-secret-value-action"
                        onClick={() => row.secret && onValue(row.secret)}
                      >
                        {text(
                          row.kind === 'variable'
                            ? 'actions.editValue'
                            : 'actions.rotate'
                        )}
                      </Button>
                      <RowMenu
                        secret={row.secret}
                        onEdit={() => row.secret && onEdit(row.secret)}
                        onDelete={() => row.secret && onDelete(row.secret)}
                      />
                    </>
                  ) : (
                    <span
                      role="img"
                      aria-label={text(
                        row.secret
                          ? row.secret.pypestreamKey && canManage
                            ? 'readOnly.pypestreamKey'
                            : 'readOnly.role'
                          : `readOnly.${row.source}`
                      )}
                      data-test-id="org-secret-read-only"
                    >
                      <Icon name="lock" size="small" aria-hidden="true" />
                    </span>
                  )}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RowMenu({
  secret,
  onEdit,
  onDelete,
}: {
  secret: OrgSecret;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const text = useSecretsText();
  const menu = useRef<DropdownWC>(null);
  const [open, setOpen] = useState(false);
  const triggerId = `org-secret-actions-${secret.id}`;

  // The DS closes the popover in its own listener, so the trigger's state follows its events.
  useEffect(() => {
    const element = menu.current;
    if (!element) return undefined;
    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);
    element.addEventListener('popover-open', onOpen);
    element.addEventListener('close', onClose);
    return () => {
      element.removeEventListener('popover-open', onOpen);
      element.removeEventListener('close', onClose);
    };
  }, []);

  const choose = (action: () => void) => () => {
    menu.current?.hide();
    action();
  };

  return (
    <>
      <IconButton
        id={triggerId}
        name="kebab"
        size="small"
        variant="subtle"
        hasPopup="menu"
        expanded={open}
        label={text('actions.menu', { name: secret.name })}
      />
      {/* React owns the portal, so the DS never moves the menu out from under it. */}
      {createPortal(
        <Dropdown
          // @ts-expect-error the DS wrapper types `ref` as its props, not the element
          ref={menu}
          trigger={triggerId}
          placement="bottom-end"
          omitPortal
          keepMounted
        >
          <Menu>
            <MenuItem test-id="org-secret-edit" onClick={choose(onEdit)}>
              {text('actions.edit')}
            </MenuItem>
            <MenuItem test-id="org-secret-delete" onClick={choose(onDelete)}>
              {text('actions.delete')}
            </MenuItem>
          </Menu>
        </Dropdown>,
        document.body
      )}
    </>
  );
}
